import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  DeleteProjectByIdParams,
  DeleteProjectByIdData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface DeleteProjectByIdState
  extends MergeRequestState<DeleteProjectByIdData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.deleteProjectById,
  isFetching: (state: ReduxState) => state.server.deleteProjectById.isFetching,
  data: (state: ReduxState) => state.server.deleteProjectById.data,
  error: (state: ReduxState) => state.server.deleteProjectById.error,
};

const initialState: DeleteProjectByIdState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/deleteProjectById';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      config,
    }: { params: DeleteProjectByIdParams; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.deleteProjectById(params, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerDeleteProjectById {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const deleteProjectById: ServerDeleteProjectById = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
